/* eslint-disable react/jsx-pascal-case */

import { useEffect, useRef } from 'react'

import { useTheme } from '@mui/material/styles'
import type { ThemedSVGImageProps } from '../../../core'

const MAX_SVG_WIDTH = 200
const MINI_SVG_WIDTH = 130

// Utility function to get an SVG element from a DOM node
const getSVGElement = (element: Element | null): SVGElement | null => {
  return element ? (element as SVGElement) : null
}

// Utility function to apply color to SVG elements
const applyColorToSVG = (elements: NodeListOf<Element>, color: string) => {
  elements.forEach((element) => {
    const svgElement = getSVGElement(element)
    if (svgElement) {
      const strokeAttr = svgElement.getAttribute('stroke')
      const fillAttr = svgElement.getAttribute('fill')

      if (strokeAttr && strokeAttr !== 'white') {
        svgElement.style.setProperty('stroke', color)
      }

      if (fillAttr) {
        const fillColor = fillAttr === 'white' ? 'white' : color
        svgElement.style.setProperty('fill', fillColor)
      }
    }
  })
}

export const ThemedSVGImage = ({ divProps, image: SVG, isMini = false }: ThemedSVGImageProps) => {
  const theme = useTheme()
  const containerRef = useRef<SVGSVGElement | null>(null)

  // If the client does not have a color scheme aka widget brand color, set the colors to the primary kyper token colors
  // Apply color scheme to SVG elements when the component mounts or the theme changes.
  // `colors` is derived inside the effect so it isn't a new reference every render.
  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current

    const colors = {
      '100': (theme as any).palette.primary.lighter,
      '200': theme.palette.primary.light,
      '300': theme.palette.primary.main,
      '400': theme.palette.primary.dark,
      '500': (theme as any).palette.primary.darker,
    }

    const overlayElements = container.querySelectorAll('.Overlay')
    const brandElements = ['brand100', 'brand200', 'brand300', 'brand400', 'brand500']

    brandElements.forEach((className) => {
      const elements = container.querySelectorAll(`.${className}`)
      const colorKey = className.replace('brand', '') as keyof typeof colors
      applyColorToSVG(elements, colors[colorKey])
    })

    // Apply the color for overlays separately (using the 300 color scheme)
    applyColorToSVG(overlayElements, colors['300'])
  }, [theme])

  return (
    <div
      style={{
        maxWidth: isMini ? MINI_SVG_WIDTH : MAX_SVG_WIDTH,
        width: '100%',
      }}
      {...divProps}
    >
      <SVG ref={containerRef} />
    </div>
  )
}
